import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppUser } from '../../../../models/AppUser';
import useUserService from '../../../../hooks/useUserService';
import { useWarehouses } from '../../../../hooks/useWarehouse';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import showLoadingToast from '../../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../../components/Toasts/ShowSuccessToast';
import { Heading, Subheading } from '../../../../components/Heading';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { Switch, SwitchField, SwitchGroup } from '../../../../components/Switch';
import { Select } from '../../../../components/Select';
import { Text } from '../../../../components/Text';
import { AppAnnouncement } from '../../../../models/AppAnnouncement';
import { useAppAnnouncements } from '../../../../hooks/useAppAnnouncement';
import { Textarea } from '../../../../components/Textarea';
import { Input } from '../../../../components/Input';
import MultipleImageUpload from '../../../../components/MultipleImageUpload';

interface FileWithPreview {
    file: File;
    id: string;
    preview?: string;
    type: 'image' | 'pdf';
}

const EditAppAnnouncementPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); // Assuming `id` is passed as a URL parameter

    const [announcement, setAnnouncement] = useState<AppAnnouncement | null>(null);
    const { fetchAnnouncementById, loading, error, updateAnnouncementWithMultipleImages } = useAppAnnouncements();
    const { warehouses } = useWarehouses();
    const [isAdmin, setIsAdmin] = useState(false)
    const [fileItems, setFileItems] = useState<FileWithPreview[]>([]);

    useEffect(() => {
        document.title = "Edit App Announcement";
    }, []);

    useEffect(() => {
        const fetchAnnouncementDetails = async () => {
            try {
                const fetchedAnnouncement = await fetchAnnouncementById(Number(id));
                if (fetchedAnnouncement) {
                    setAnnouncement(fetchedAnnouncement);
                }
            } catch (err) {
                showErrorToast('Failed to fetch user details.');
            }
        };

        fetchAnnouncementDetails();
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setAnnouncement(prev => prev ? { ...prev, [name]: value } : null);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!announcement) return;
        if (!announcement.title || !announcement.content) {
            showErrorToast("Please fill in all field before submit");
            return;
        }

        try {
            const announcementToUpdate = {
                ...announcement,
                created_user_id: announcement.created_user_id!.toString(),
            };

            let res;
            if (fileItems.length > 0) {
                const files = fileItems.map(item => item.file);
                res = await updateAnnouncementWithMultipleImages(announcement.id, announcementToUpdate, files);
            } else {
                // If no new files, you might want to use the regular updateAnnouncement method
                // For now, we'll use the multiple images method with empty array
                res = await updateAnnouncementWithMultipleImages(announcement.id, announcementToUpdate, []);
            }

            if (res?.dbsuccess) {
                showLoadingToast(`Updating Announcement ...`, 100);
                setTimeout(() => {
                    navigate('/gp/app/announcements');
                    showSuccessToast(`Announcements updated successfully`);
                }, 1000);
            } else {
                showErrorToast(`Error updating announcement`);
            }
        } catch (err) {
            showErrorToast('Failed to update announcement.');
        }
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>{error}</p>;
    if (!announcement) return <p>Announcement not found.</p>;
    if (!warehouses) return <p>Warehouses not found.</p>;

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Edit App Announcement</Heading>
                <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="reset" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="submit" className="ml-3" disabled={(!announcement.title || !announcement.content)}>Save</Button>
                </div>
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Announcement Details</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            A brief summary highlighting key personal details.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Title</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        type="text"
                                        aria-label="Title"
                                        name="title"
                                        value={announcement.title?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Content</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Textarea
                                        aria-label="Content"
                                        name="content"
                                        value={announcement.content?.toString()}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                        <div className="sm:col-span-full">
                            <section className="">
                                <div className="space-y-1">
                                    <Subheading>Current Files</Subheading>
                                </div>
                                <div className="mt-2 space-y-4">
                                    {/* Display current files */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                        {announcement.image_url && (
                                            <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                <div className="aspect-square bg-gray-50 relative">
                                                    {announcement.image_url.toLowerCase().endsWith('.pdf') ? (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <div className="text-center">
                                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                </svg>
                                                                <Text className="text-xs text-gray-500 mt-1">PDF</Text>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={announcement.image_url}
                                                            alt="Current file"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                    <div className="absolute top-2 left-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url.toLowerCase().endsWith('.pdf')
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                            {announcement.image_url.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <Text className="text-xs text-gray-900 font-medium">Main File</Text>
                                                </div>
                                            </div>
                                        )}
                                        {announcement.image_url_2 && (
                                            <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                <div className="aspect-square bg-gray-50 relative">
                                                    {announcement.image_url_2.toLowerCase().endsWith('.pdf') ? (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <div className="text-center">
                                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                </svg>
                                                                <Text className="text-xs text-gray-500 mt-1">PDF</Text>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={announcement.image_url_2}
                                                            alt="File 2"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                    <div className="absolute top-2 left-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_2.toLowerCase().endsWith('.pdf')
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                            {announcement.image_url_2.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <Text className="text-xs text-gray-900 font-medium">File 2</Text>
                                                </div>
                                            </div>
                                        )}
                                        {announcement.image_url_3 && (
                                            <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                <div className="aspect-square bg-gray-50 relative">
                                                    {announcement.image_url_3.toLowerCase().endsWith('.pdf') ? (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <div className="text-center">
                                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                </svg>
                                                                <Text className="text-xs text-gray-500 mt-1">PDF</Text>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={announcement.image_url_3}
                                                            alt="File 3"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                    <div className="absolute top-2 left-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_3.toLowerCase().endsWith('.pdf')
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                            {announcement.image_url_3.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <Text className="text-xs text-gray-900 font-medium">File 3</Text>
                                                </div>
                                            </div>
                                        )}
                                        {announcement.image_url_4 && (
                                            <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                <div className="aspect-square bg-gray-50 relative">
                                                    {announcement.image_url_4.toLowerCase().endsWith('.pdf') ? (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <div className="text-center">
                                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                </svg>
                                                                <Text className="text-xs text-gray-500 mt-1">PDF</Text>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={announcement.image_url_4}
                                                            alt="File 4"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                    <div className="absolute top-2 left-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_4.toLowerCase().endsWith('.pdf')
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                            {announcement.image_url_4.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}</span>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <Text className="text-xs text-gray-900 font-medium">File 4</Text>
                                                </div>
                                            </div>
                                        )}
                                        {announcement.image_url_5 && (
                                            <div className="relative bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                                                <div className="aspect-square bg-gray-50 relative">
                                                    {announcement.image_url_5.toLowerCase().endsWith('.pdf') ? (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <div className="text-center">
                                                                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                                </svg>
                                                                <Text className="text-xs text-gray-500 mt-1">PDF</Text>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={announcement.image_url_5}
                                                            alt="File 5"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    )}
                                                    <div className="absolute top-2 left-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${announcement.image_url_5.toLowerCase().endsWith('.pdf')
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                            {announcement.image_url_5.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}</span>
                                                    </div>
                                                </div>
                                                <div className="p-3">
                                                    <Text className="text-xs text-gray-900 font-medium">File 5</Text>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* New file upload */}
                                    <div className="mt-6">
                                        <Subheading>Add New Files (optional)</Subheading>
                                        <Text className="text-sm text-gray-600 mt-1">
                                            Upload new files to replace or add to existing ones
                                        </Text>
                                        <MultipleImageUpload
                                            files={fileItems}
                                            onFilesChange={setFileItems}
                                            maxFiles={5}
                                            className="mt-2"
                                        />
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="flex justify-end gap-4 my-6">
                <Button type="reset" plain onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button type="submit" disabled={(!announcement.title || !announcement.content)}>Save</Button>
            </div>
        </form>
    );
};

export default EditAppAnnouncementPage;
